import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CubeIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline'
import { inventoryService } from '../../services/inventoryService'
import { productService } from '../../services/productService'

interface InventoryItem {
  serial_id: number
  serial_number: string
  status: string
  current_location: string
  products: {
    product_id: number
    product_name: string
    sku: string
    brands: { brand_name: string } | null
    categories?: { category_name: string } | null
  }
}

export const InventoryPage = () => {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [activeTab, setActiveTab] = useState<'all' | 'low' | 'valuation'>('all')

  const { data, isLoading } = useQuery({
    queryKey: ['inventory', search, statusFilter],
    queryFn: () => inventoryService.getAll({
      status: statusFilter || undefined,
      search: search || undefined,
      page: 1,
      limit: 50
    }),
  })

  const { data: lowStockData, isLoading: lowStockLoading } = useQuery({
    queryKey: ['inventory', 'low-stock'],
    queryFn: () => inventoryService.getLowStock(),
  })

  const { data: valuationData, isLoading: valuationLoading } = useQuery({
    queryKey: ['inventory', 'valuation'],
    queryFn: () => inventoryService.getValuation(),
  })

  const items: InventoryItem[] = data?.data?.data?.items || []

  // No stock units matched — check if the product itself exists so we can
  // show "0 units" with a shortcut into Adjust Stock instead of a dead end.
  const { data: zeroStockData } = useQuery({
    queryKey: ['inventory', 'zero-stock-lookup', search],
    queryFn: () => productService.getAll({ search, limit: 5 }),
    enabled: Boolean(search) && !isLoading && items.length === 0,
  })
  const zeroStockMatches = zeroStockData?.data?.data?.items || []
  const lowStock = lowStockData?.data?.data || []
  const valuation = valuationData?.data?.data

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800'
      case 'sold': return 'bg-blue-100 text-blue-800'
      case 'consigned': return 'bg-yellow-100 text-yellow-800'
      case 'reserved': return 'bg-purple-100 text-purple-800'
      case 'defective': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
          <p className="text-sm text-gray-500 mt-1">
            Track and manage your stock levels
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/inventory/adjust"
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Adjust Stock
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <CubeIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Units</p>
              <p className="text-2xl font-bold text-gray-900">
                {valuation?.summary?.unit_count || 0}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-50 rounded-lg">
              <ArrowTrendingUpIcon className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Stock Value</p>
              <p className="text-2xl font-bold text-gray-900">
                KSh {valuation?.summary?.total_value?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-50 rounded-lg">
              <BanknotesIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Potential Profit</p>
              <p className="text-2xl font-bold text-gray-900">
                KSh {valuation?.summary?.potential_profit?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>
        <Link 
          to="/inventory?filter=low"
          className="bg-white rounded-xl shadow border border-gray-100 p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-yellow-50 rounded-lg">
              <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Low Stock Items</p>
              <p className="text-2xl font-bold text-gray-900">{lowStock.length}</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow border border-gray-100">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {[
              { id: 'all', label: 'All Inventory' },
              { id: 'low', label: `Low Stock (${lowStock.length})` },
              { id: 'valuation', label: 'Valuation' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-4">
          {activeTab === 'all' && (
            <>
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1 relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by brand, product, or category..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Status</option>
                  <option value="available">Available</option>
                  <option value="sold">Sold</option>
                  <option value="consigned">Consigned</option>
                  <option value="reserved">Reserved</option>
                  <option value="defective">Defective</option>
                </select>
              </div>

              {/* Inventory Table */}
              {isLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : items.length === 0 && zeroStockMatches.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {zeroStockMatches.map((product: any) => (
                    <div key={product.product_id} className="flex items-center justify-between py-4">
                      <div>
                        <Link
                          to={`/products/${product.product_id}`}
                          className="text-sm font-medium text-gray-900 hover:text-primary-600"
                        >
                          {product.product_name}
                        </Link>
                        <p className="text-xs text-gray-500">{product.sku}</p>
                        <p className="text-sm text-gray-600 mt-1">0 units in stock</p>
                      </div>
                      <Link
                        to={`/inventory/adjust?product_id=${product.product_id}`}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        <ArrowPathIcon className="w-4 h-4" />
                        Add Stock
                      </Link>
                    </div>
                  ))}
                </div>
              ) : items.length === 0 ? (
                <div className="text-center py-12">
                  <CubeIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No inventory items found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serial Number</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {items.map((item) => (
                        <tr key={item.serial_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-mono">{item.serial_number}</td>
                          <td className="px-4 py-3">
                            <Link 
                              to={`/products/${item.products.product_id}`}
                              className="text-sm font-medium text-gray-900 hover:text-primary-600"
                            >
                              {item.products.product_name}
                            </Link>
                            <p className="text-xs text-gray-500">{item.products.sku}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status)}`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{item.current_location}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {activeTab === 'low' && (
            <div>
              {lowStockLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : lowStock.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CubeIcon className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-gray-900 font-medium">All stock levels are healthy</p>
                  <p className="text-gray-500 text-sm mt-1">No products below reorder level</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Available</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reorder Level</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {lowStock.map((product: any) => (
                        <tr key={product.product_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <Link 
                              to={`/products/${product.product_id}`}
                              className="text-sm font-medium text-gray-900 hover:text-primary-600"
                            >
                              {product.product_name}
                            </Link>
                            <p className="text-xs text-gray-500">{product.sku}</p>
                          </td>
                          <td className="px-4 py-3 text-sm">{product.brands?.brand_name}</td>
                          <td className="px-4 py-3">
                            <span className={`font-medium ${
                              product._count.serial_numbers === 0 ? 'text-red-600' : 'text-yellow-600'
                            }`}>
                              {product._count.serial_numbers}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">{product.reorder_level}</td>
                          <td className="px-4 py-3">
                            <Link
                              to={`/purchases/new?product=${product.product_id}`}
                              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                            >
                              Order Stock →
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'valuation' && (
            <div>
              {valuationLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost Value</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Retail Value</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {valuation?.details?.map((item: any) => (
                        <tr key={item.product_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-gray-900">{item.product_name}</p>
                            <p className="text-xs text-gray-500">{item.sku}</p>
                          </td>
                          <td className="px-4 py-3 text-sm">{item.quantity}</td>
                          <td className="px-4 py-3 text-sm">KSh {item.cost_value.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm">KSh {item.retail_value.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-green-600">
                            KSh {item.potential_profit.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
